import React, { useEffect, useState, useRef, CSSProperties } from 'react';
import { Button, FormInstance, Modal, Row, message } from 'antd';
import {
  AudioOutlined,
  CloseOutlined,
  PlusCircleTwoTone,
  RightOutlined,
} from '@ant-design/icons';
import './index.scss';
import CommonForm from '../CommonForm';
import RemoteCallApi from './service';
import { HttpStatusCode } from '@/fetch/core/constant';
import { debounce } from '@/utils';
import { reportSgmEvent, SgmEventType } from '@/utils/sgm';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
interface Props {
  vehicleName: string;
  visible: boolean;
  setVisible: AnyFunc;
  disabled?: boolean;
  size?: SizeType;
  style?: CSSProperties;
}

interface RemoteCallFormProps {
  formInstance: any;
  wordList: {
    id: number;
    content: string;
  }[];
  setWordList: AnyFunc;
}

const requestApi = new RemoteCallApi();

const RemoteCallForm = (props: RemoteCallFormProps) => {
  const [visible, setVisible] = useState(false);
  const { formInstance, wordList, setWordList } = props;
  const formConfig: FormConfig = {
    data: [
      {
        cmpType: 'textarea',
        fieldName: 'content',
        label: '喊话内容',
        placeholder: '请填写喊话内容',
        maxLength: 30,
        validatorRules: [
          {
            required: true,
            message: '请先输入喊话内容~',
          },
          {
            type: 'string',
            max: 30,
          },
        ],
      },
    ],
  };

  useEffect(() => {
    getWordList();
  }, []);

  const getWordList = async () => {
    const res = await requestApi.getWordList();
    if (res.code === HttpStatusCode.Success) {
      setWordList(res.data);
    } else {
      message.error('获取常用语列表失败');
    }
  };

  const addWord = async () => {
    try {
      if (formInstance.current) {
        const formValue = await formInstance.current.validateFields();

        const res = await requestApi.addWord(formValue.content);
        if (res.code === HttpStatusCode.Success) {
          getWordList();
          message.success('添加常用语成功！');
          setVisible(true);
        }
      }
    } catch {
      message.error('请先输入喊话内容~');
    }
  };

  const removeWord = async (id: number) => {
    const res = await requestApi.deleteWord(id);
    if (res.code === HttpStatusCode.Success) {
      getWordList();
      message.success('常用语删除成功～');
    } else {
      message.error('常用语删除失败');
    }
  };

  return (
    <>
      <CommonForm
        config={formConfig}
        layout={'horizontal'}
        getFormInstance={(formRef: FormInstance) => {
          formInstance.current = formRef;
        }}
      />
      <Row className='operation_area'>
        <Button className='custom_round_btn' onClick={addWord}>
          <PlusCircleTwoTone twoToneColor={'#FF0000'} />
          添加常用语
        </Button>
        <Button
          className='custom_round_btn'
          onClick={() => {
            setVisible(!visible);
          }}
        >
          常用语
          <RightOutlined />
        </Button>
      </Row>
      <Row
        className='collected_word_list'
        style={visible ? { display: 'block' } : { display: 'none' }}
      >
        <div className='list_area'>
          {wordList.map(
            (
              item: {
                id: number;
                content: string;
              },
              index: number,
            ) => {
              return (
                <div
                  className='word'
                  key={item.id}
                  onClick={async () => {
                    if (formInstance.current) {
                      const formValue =
                        formInstance.current.getFieldValue('content');

                      formInstance.current.setFieldValue(
                        'content',
                        formValue ? formValue + item.content : item.content,
                      );
                      await formInstance.current.validateFields();
                    }
                  }}
                >
                  <span className='word_content'>{item.content}</span>
                  <span className='delete_icon'>
                    <CloseOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        removeWord(item.id);
                      }}
                    />
                  </span>
                </div>
              );
            },
          )}
        </div>
      </Row>
    </>
  );
};

const RemoteCallModal = (props: Props) => {
  const { vehicleName, disabled, size, style, visible, setVisible } = props;
  const [wordList, setWordList] = useState<
    {
      id: number;
      content: string;
    }[]
  >([
    {
      id: 1,
      content: 'Hello',
    },
  ]);
  const formInstance = useRef<any>(null);
  const reportRemoteCall = (
    type: SgmEventType,
    action: string,
    result: string,
    reason?: string,
    cost1?: number,
  ) => {
    reportSgmEvent({
      type,
      code: 'monitor_remote_control_function',
      msg: {
        page: 'remote_control',
        vehicleName,
        action,
        trigger: 'click',
        requestId: null,
        result,
        reason,
      },
      cost1,
    });
  };
  const openRemoteCallModal = () => {
    reportRemoteCall(SgmEventType.INFO, 'remote_call_open', 'opened');
    setVisible(true);
  };
  const sendRemoteCall = debounce(async () => {
    let formValue: { content: string };
    try {
      if (!formInstance.current) {
        reportRemoteCall(SgmEventType.WARN, 'remote_call_submit', 'blocked', 'form_unavailable');
        return;
      }
      formValue = await formInstance.current.validateFields();
    } catch {
      reportRemoteCall(SgmEventType.WARN, 'remote_call_submit', 'blocked', 'form_validation_failed');
      return;
    }

    const startedAt = Date.now();
    reportRemoteCall(SgmEventType.INFO, 'remote_call_submit', 'submitted');
    try {
      const res = await requestApi.sendRemoteCall({
        vehicleName: vehicleName,
        message: formValue.content,
      });

      if (res.code === HttpStatusCode.Success) {
        reportRemoteCall(SgmEventType.INFO, 'remote_call_result', 'success', undefined, Date.now() - startedAt);
        message.success('下发远程喊话指令成功！');
      } else {
        reportRemoteCall(SgmEventType.ERROR, 'remote_call_result', 'failed', 'business_failed', Date.now() - startedAt);
        message.error('发送远程喊话指令失败');
      }
    } catch {
      reportRemoteCall(SgmEventType.ERROR, 'remote_call_result', 'failed', 'request_error', Date.now() - startedAt);
    }
  }, 500);
  return (
    <>
      <Button
        size={size ?? 'small'}
        onClick={openRemoteCallModal}
        disabled={disabled}
      >
        <AudioOutlined />
        远程喊话
      </Button>
      {visible ? (
        <Modal
          maskClosable={false}
          visible={visible}
          title='远程喊话'
          destroyOnHidden
          onCancel={() => {
            reportRemoteCall(SgmEventType.WARN, 'remote_call_cancel', 'cancelled', 'user_cancelled');
            setVisible(false);
          }}
          onOk={sendRemoteCall}
          style={style}
        >
          <RemoteCallForm
            wordList={wordList}
            setWordList={setWordList}
            formInstance={formInstance}
          />
        </Modal>
      ) : (
        ''
      )}
    </>
  );
};
export default React.memo(RemoteCallModal);
